import { Site } from '@domain/graphql.types';
import { Box, Button, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeOfDay, getOpeningState, sortAmenitiesByWalkingDistance, walkingMinutes } from '../components/amenities';
import {
  avgReliabilityScore,
  buildHourlyUtilization,
  currentHourInTZ,
  extractPricing,
  getAvailability,
  getEvseMaxPowerKw,
  getPortStatus,
  getRealTimePorts,
  getWaitMinutes,
  PortStatus,
  reliabilityLevel,
} from '../components/realTimeLocation';
import { ChartTitle, HourlyUtilizationChart } from '../components/StationCharts';
import { siteLookupTokens } from '../siteLookupTokens';

const { color } = siteLookupTokens;

const PREVIEW_COUNT = { ports: 4, amenities: 3 };
/** Below this width the five port columns no longer fit with "100 · Excellent" and "Out of service" badges. */
const NARROW_PORTS = '@container (max-width: 359.98px)';

const portStatusColors: Record<PortStatus, { background: string; text: string }> = {
  available: color.availability.available,
  charging: color.availability.busy,
  outOfService: color.availability.unavailable,
  unknown: color.availability.unknown,
};

const Tile = ({ label, children, colors }: { label: string; children: ReactNode; colors?: { background: string; text: string } }) => (
  <Box sx={{ border: `1px solid ${color.border}`, borderRadius: '8px', paddingX: 1.25, paddingY: 1, minWidth: 0 }}>
    <Typography fontSize={12} color={color.text.muted}>
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        display: 'inline-block',
        marginTop: 0.25,
        fontSize: 16,
        fontWeight: 600,
        color: colors?.text ?? color.text.primary,
        backgroundColor: colors?.background,
        borderRadius: '4px',
        paddingX: colors ? 0.75 : 0,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const StatusBadge = ({ status }: { status: PortStatus }) => {
  const { t } = useTranslation();
  const { background, text } = portStatusColors[status];
  return (
    <Box
      component="span"
      sx={{ backgroundColor: background, color: text, borderRadius: '4px', fontSize: 12, fontWeight: 600, paddingX: 0.75, whiteSpace: 'nowrap' }}
    >
      {t(`siteLookup.detail.realtime.portStatus.${status}`)}
    </Box>
  );
};

/** "97 · Excellent", or "N/A": the level word is always there so the status is not told by colour alone. */
const formatReliability = (score: number | null, t: TFunction) => {
  const level = reliabilityLevel(score);
  const label = t(`siteLookup.detail.realtime.reliabilityLevel.${level}`);
  return {
    colors: color.reliability[level],
    text: score === null ? label : t('siteLookup.detail.realtime.reliabilityScore', { score: Math.round(score), level: label }),
  };
};

const ReliabilityBadge = ({ score }: { score: number | null }) => {
  const { t } = useTranslation();
  const { colors, text } = formatReliability(score, t);
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: colors.background,
        color: colors.text,
        borderRadius: '4px',
        fontSize: 12,
        fontWeight: 600,
        paddingX: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={visuallyHidden}>
        {t('siteLookup.detail.realtime.portReliability')}{' '}
      </Box>
      {text}
    </Box>
  );
};

/** "View all N …" below a preview list, expanding it in place. */
const ShowAllButton = ({ controls, expanded, label, onClick }: { controls: string; expanded: boolean; label: string; onClick: () => void }) => (
  <Button
    size="small"
    aria-expanded={expanded}
    aria-controls={controls}
    onClick={onClick}
    sx={{ color: color.link, fontWeight: 600, textTransform: 'none', marginLeft: -0.5, marginTop: 0.5 }}
  >
    {label}
  </Button>
);

/** Real-time sheet: availability tiles, typical utilization by hour, ports and nearby amenities. */
const RealTimeStationDetail = ({ site }: { site: Site }) => {
  const { t, i18n } = useTranslation();
  const rtl = site.realTimeLocation;
  const timeZone = rtl?.timezone || 'UTC';
  const portsListId = useId();
  const amenitiesListId = useId();
  const [showAllPorts, setShowAllPorts] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const availability = getAvailability(rtl);
  const waitMinutes = getWaitMinutes(rtl);
  const pricing = extractPricing(rtl?.chargingPool, timeZone);
  const reliability = formatReliability(avgReliabilityScore(rtl?.chargingPool), t);
  const ports = useMemo(() => getRealTimePorts(rtl), [rtl]);
  const hourly = useMemo(() => buildHourlyUtilization(rtl), [rtl]);
  const amenities = useMemo(() => sortAmenitiesByWalkingDistance(site.amenities), [site.amenities]);

  if (!rtl) {
    return (
      <Typography fontSize={14} color={color.text.secondary} paddingY={2}>
        {t('siteLookup.detail.realtime.noData')}
      </Typography>
    );
  }

  const price =
    pricing.pricingUnit === 'kWh'
      ? `$${pricing.pricingNumeric.toFixed(2)}`
      : pricing.pricingUnit === 'h'
        ? t('siteLookup.detail.realtime.pricePerHour', { price: `$${pricing.pricingNumeric.toFixed(2)}` })
        : '—';
  const wait =
    waitMinutes === null ? '—' : waitMinutes === 0 ? t('siteLookup.detail.realtime.noWait') : t('siteLookup.detail.realtime.minutes', { count: waitMinutes });

  const visiblePorts = showAllPorts ? ports : ports.slice(0, PREVIEW_COUNT.ports);
  const visibleAmenities = showAllAmenities ? amenities : amenities.slice(0, PREVIEW_COUNT.amenities);

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(110px, 1fr))" gap={1}>
        <Tile label={t('siteLookup.detail.realtime.available')} colors={availability ? color.availability[availability.level] : undefined}>
          {availability ? `${availability.available} / ${availability.total}` : '—'}
        </Tile>
        <Tile label={t('siteLookup.detail.realtime.wait')}>{wait}</Tile>
        <Tile label={t('siteLookup.detail.realtime.price')}>{price}</Tile>
        <Tile label={t('siteLookup.detail.realtime.reliability')} colors={reliability.colors}>
          {reliability.text}
        </Tile>
      </Box>

      <HourlyUtilizationChart hourly={hourly} currentHour={currentHourInTZ(new Date(), timeZone)} />

      {ports.length > 0 && (
        <Box sx={{ containerType: 'inline-size' }}>
          <ChartTitle title={t('siteLookup.detail.realtime.ports')} />
          {/* One grid for every row (subgrid): the power and the badges line up whatever the width of the badges above. */}
          <Box
            component="ol"
            id={portsListId}
            sx={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gridTemplateColumns: '20px minmax(0, 1fr) auto auto auto',
              columnGap: 1,
              [NARROW_PORTS]: { gridTemplateColumns: '20px minmax(0, 1fr) auto' },
            }}
          >
            {visiblePorts.map(({ evse, reliabilityScore }, index) => {
              const power = getEvseMaxPowerKw(evse);
              return (
                <Box
                  component="li"
                  key={evse.uid}
                  sx={{
                    gridColumn: '1 / -1',
                    display: 'grid',
                    gridTemplateColumns: 'subgrid',
                    alignItems: 'center',
                    // the badges keep their own width, left-aligned in their column
                    justifyItems: 'start',
                    paddingY: 0.75,
                    borderTop: index ? `1px solid ${color.border}` : undefined,
                    // narrow sheet: the badges go on a second line, under the id and the power
                    [NARROW_PORTS]: {
                      rowGap: 0.5,
                      '& > :nth-child(4)': { gridRow: 2, gridColumn: 2 },
                      '& > :nth-child(5)': { gridRow: 2, gridColumn: 3 },
                    },
                  }}
                >
                  <Typography fontSize={13} fontWeight={600} color={color.text.primary}>
                    {index + 1}
                  </Typography>
                  <Typography fontSize={12} color={color.text.muted} minWidth={0} maxWidth="100%" sx={{ overflowWrap: 'anywhere' }}>
                    {evse.evseId || evse.name || '—'}
                  </Typography>
                  <Typography fontSize={13} color={color.text.primary} whiteSpace="nowrap">
                    {power === null ? '—' : t('siteLookup.list.power', { power })}
                  </Typography>
                  <ReliabilityBadge score={reliabilityScore} />
                  <StatusBadge status={getPortStatus(evse.status)} />
                </Box>
              );
            })}
          </Box>
          {ports.length > PREVIEW_COUNT.ports && (
            <ShowAllButton
              controls={portsListId}
              expanded={showAllPorts}
              onClick={() => setShowAllPorts((all) => !all)}
              label={showAllPorts ? t('siteLookup.detail.showLess') : t('siteLookup.detail.realtime.viewAllPorts', { count: ports.length })}
            />
          )}
        </Box>
      )}

      {amenities.length > 0 && (
        <Box>
          <ChartTitle title={t('siteLookup.detail.realtime.nearby')} />
          <Box component="ul" id={amenitiesListId} sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {visibleAmenities.map((amenity, index) => {
              const opening = getOpeningState(amenity.openingHours, timeZone);
              const hours =
                opening.kind === 'open'
                  ? t('siteLookup.detail.realtime.closes', { time: formatTimeOfDay(opening.closesAt, i18n.language) })
                  : opening.kind === 'opensLater'
                    ? t('siteLookup.detail.realtime.opens', { time: formatTimeOfDay(opening.opensAt, i18n.language) })
                    : opening.kind === 'closed'
                      ? t('siteLookup.detail.realtime.closed')
                      : null;
              return (
                <Box
                  component="li"
                  key={`${amenity.name}-${index}`}
                  display="flex"
                  alignItems="baseline"
                  gap={1}
                  paddingY={0.75}
                  borderTop={index ? `1px solid ${color.border}` : undefined}
                >
                  <Typography fontSize={13} color={color.text.primary} flex={1} minWidth={0} sx={{ overflowWrap: 'anywhere' }}>
                    {amenity.name}
                  </Typography>
                  <Typography fontSize={12} color={color.text.secondary} whiteSpace="nowrap">
                    {[t('siteLookup.detail.realtime.walk', { count: walkingMinutes(amenity.distanceWalkingMeters) }), hours].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {amenities.length > PREVIEW_COUNT.amenities && (
            <ShowAllButton
              controls={amenitiesListId}
              expanded={showAllAmenities}
              onClick={() => setShowAllAmenities((all) => !all)}
              label={showAllAmenities ? t('siteLookup.detail.showLess') : t('siteLookup.detail.realtime.viewAll')}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default RealTimeStationDetail;
